// dashboard/frontend/src/components/Campaign/CampaignContacts.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    MenuItem,
    Alert,
    Snackbar,
    CircularProgress,
    Pagination,
    Tooltip,
    FormControlLabel,
    Switch,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    CloudUpload as CloudUploadIcon,
    PendingOutlined as PendingIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    Error as ErrorIcon,
    Email as EmailIcon,
    Visibility as VisibilityIcon,
    Reply as ReplyIcon,
    DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import LanguageIcon from '@mui/icons-material/Language';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import moment from 'moment';
import api from '../../api/axios';
import ContactImport from './ContactImport';
import ContactEditor from '../Contacts/ContactEditor';  // Add this import at the top
import WebContactImporter from './WebContactImporter';
import CampaignContactStatusDialog from './CampaignContactStatusDialog';

const CampaignContacts = ({ campaignId, campaign }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openImportDialog, setOpenImportDialog] = useState(false);
    const [showWebImporter, setShowWebImporter] = useState(false);
    const [editContact, setEditContact] = useState(null);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [enrichmentPending, setEnrichmentPending] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [bouncedOnly, setBouncedOnly] = useState(false);

    const initialFormData = {
        first_name: '',
        last_name: '',
        email: '',
        company_name: '',
        title: '',
        linkedin_url: '',
        website: '',
        phone: '',
        contact_snapshot: '',
        business_context: '',
        priority: 2,
    };
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const statusConfig = {
        pending: { color: 'default', icon: <PendingIcon /> },
        processed: { color: 'primary', icon: <CheckCircleIcon /> },
        blocked: { color: 'error', icon: <BlockIcon /> },
        error: { color: 'error', icon: <ErrorIcon /> },
        email_sent: { color: 'info', icon: <EmailIcon /> },
        opened: { color: 'success', icon: <VisibilityIcon /> },
        replied: { color: 'success', icon: <ReplyIcon /> },
        completed: { color: 'success', icon: <DoneAllIcon /> }
    };

    const fetchContacts = useCallback(async () => {
        try {
            //setLoading(true);
            const startIndex = page * rowsPerPage;
            
            // Log pagination details
            console.log('Fetching page:', page + 1);
            console.log('Page size:', rowsPerPage);
            console.log('Expected start index:', startIndex);
    
            const response = await api.get(`/campaign-contacts/`, {
                params: {
                    campaign_id: campaignId,
                    page: page + 1,
                    page_size: rowsPerPage,
                    bounced: bouncedOnly ? 1 : undefined
                }
            });
    
            // Validate received data
            const newContacts = response.data.results;
            console.log('Received contacts:', newContacts.length);
            
            // Check for duplicates
            const contactIds = new Set();
            const duplicates = newContacts.filter(contact => {
                if (contactIds.has(contact.id)) {
                    console.warn('Duplicate contact found:', contact.id);
                    return true;
                }
                contactIds.add(contact.id);
                return false;
            });
    
            if (duplicates.length > 0) {
                console.warn('Duplicate contacts found:', duplicates);
            }
    
            setContacts(newContacts);
            setTotalCount(response.data.count);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            setError('Failed to load contacts');
        } finally {
            setLoading(false);
        }
    }, [campaignId, page, rowsPerPage, bouncedOnly]);

    const handleChangePage = (event, newPage) => {
        console.log('Changing to page:', newPage);
        if (newPage >= 0 && newPage * rowsPerPage < totalCount) {
            setPage(newPage);
        }
    };
    
    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        console.log('Changing rows per page to:', newRowsPerPage);
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to first page
    };

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts, rowsPerPage]);
    
    const handleOpenDialog = async (assignment = null) => {
        try {
            if (assignment) {
                // Log the assignment to see its structure
                console.log('Assignment data:', assignment);
                
                // Fetch full contact details using the contact ID from the assignment
                const contactId = assignment.id;  // or assignment.contact_id depending on your data structure
                const response = await api.get(`/user-contacts/${contactId}/`);
                const fullContact = response.data;
                console.log('Fetched contact data:', fullContact);
                setEditContact(fullContact);
            } else {
                setEditContact(null);
            }
            setOpenDialog(true);
        } catch (error) {
            console.error('Error fetching contact details:', error);
        }
    };

    const handleSaveContact = async (contactData) => {
        try {
            if (editContact) {
                // Get the current assignment for this contact to preserve status
                const currentAssignment = contacts.find(c => c.id === editContact.id);
                const currentStatus = currentAssignment?.status || 'processing';
    
                // Update existing contact
                const response = await api.patch(
                    `/user-contacts/${editContact.id}/`,
                    contactData
                );
                
                // Update local state while preserving the status
                const updatedContact = {
                    ...response.data,
                    status: currentStatus  // Preserve the existing status
                };
                
                setContacts(contacts.map(contact => 
                    contact.id === editContact.id ? updatedContact : contact
                ));
                
                setSnackbar({
                    open: true,
                    message: 'Contact updated successfully',
                    severity: 'success'
                });
            } else {
                // Create new contact
                const contactResponse = await api.post('/user-contacts/', contactData);
                const newContact = contactResponse.data;

                // Create campaign assignment
                await api.post('/campaign-contact-assignments/', {
                    campaign: campaignId,
                    contact: newContact.id,
                    status: 'pending'
                });
                
                await fetchContacts(); // Refresh the list
            }
            
            setOpenDialog(false);
        } catch (error) {
            console.error('Error saving contact:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.detail || 'Error saving contact',
                severity: 'error'
            });
        }
    };
    
    const handleStatusUpdate = async (campaignId, contactId, newStatus) => {
        try {
            // Update to use campaign-contacts endpoint
            await api.patch(`/campaign-contacts/${contactId}/update_status/`, {
                campaign_id: campaignId,
                status: newStatus
            });
            
            // Update local state
            setContacts(contacts.map(contact => 
                contact.id === contactId 
                    ? { ...contact, status: newStatus }
                    : contact
            ));
    
            setSnackbar({
                open: true,
                message: 'Status updated successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error updating status:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.detail || 'Error updating status',
                severity: 'error'
            });
        }
    };

    const handleEnrichCompanyProfiles = async () => {
    setLoading(true);
    try {
        // Assuming you have campaignId in scope
        await api.post(`/campaigns/${campaignId}/enrich_contacts/`);
        setSnackbar({
        open: true,
        message: "Company enrichment started! Profiles will update as they finish.",
        severity: "info"
        });
    } catch (e) {
        setSnackbar({
        open: true,
        message: "Error starting enrichment.",
        severity: "error"
        });
    }
    setLoading(false);
    };

    useEffect(() => {
        api.get(`/campaigns/${campaignId}/enrichment_status/`)
            .then(res => {
                setEnrichmentPending(res.data.pending);
                setPendingCount(res.data.pending_count);
            })
            .catch(e => {
                setEnrichmentPending(false);
                setPendingCount(0);
            });
    }, [campaignId]);

    const handleDelete = async (contactId) => {
        if (window.confirm('Are you sure you want to delete this contact?')) {
            try {
                await api.delete(`/campaign-contacts/${contactId}/`);
                fetchContacts();
            } catch (error) {
                setError('Failed to delete contact');
            }
        }
    };

    const handleRemoveFromCampaign = async (contactId) => {
        try {
            await api.delete(`/campaign-contacts/${campaignId}/remove_contact/${contactId}/`);
            fetchContacts();
        } catch (error) {
            console.error('Error removing contact from campaign:', error);
        }
    };

    const handleImportComplete = () => {
        setOpenImportDialog(false);
        fetchContacts();
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box mb={3} display="flex" justifyContent="space-between">
                <Typography variant="h6">
                    Campaign Contacts ({totalCount})
                </Typography>
                <Box display="flex" alignItems="center">
                    <FormControlLabel
                        sx={{ mr: 2 }}
                        control={
                            <Switch
                                size="small"
                                color="error"
                                checked={bouncedOnly}
                                onChange={(e) => { setBouncedOnly(e.target.checked); setPage(0); }}
                            />
                        }
                        label="Bounced only"
                    />
                    <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => setOpenImportDialog(true)}
                        sx={{ mr: 2 }}
                    >
                        CSV Import
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<LanguageIcon />}
                        onClick={() => setShowWebImporter(true)}
                        sx={{ mr: 2 }}
                    >
                        Web Import
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Contact
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleEnrichCompanyProfiles}
                        startIcon={<AutorenewIcon />}
                        sx={{ ml: 2 }}
                    >
                    Enrich Company Profiles
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {enrichmentPending && (
            <Alert severity="info" sx={{ mb: 2 }}>
                Company enrichment in progress for {pendingCount} compan{pendingCount === 1 ? 'y' : 'ies'}. Personalization may be limited until complete.
            </Alert>
            )}

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : (
                            contacts.map((contact) => (
                            <TableRow key={contact.id}>
                                <TableCell>
                                    {contact.first_name} {contact.last_name}
                                </TableCell>
                                <TableCell>
                                    {contact.email}
                                    {contact.bounced_at && (
                                        <Tooltip title={contact.bounce_reason || 'Hard bounce'}>
                                            <Chip
                                                label="Bounced"
                                                color="error"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell>{contact.company_name}</TableCell>
                                <TableCell>{contact.title}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={contact.error_message || contact.status}
                                        color={statusConfig[contact.status]?.color || 'default'}
                                        icon={statusConfig[contact.status]?.icon}
                                        size="small"
                                        onClick={() => {
                                            setSelectedContact(contact);
                                            setStatusDialogOpen(true);
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                opacity: 0.8,
                                                boxShadow: 1
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenDialog(contact)}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveFromCampaign(contact.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelDisplayedRows={({ from, to, count }) => (
                        `${from}–${to} of ${count}`
                    )}
                />
            </TableContainer>

            <ContactEditor
                campaignId={campaignId}
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                contact={editContact}
                onSave={handleSaveContact}
            />

            {/* Add Status Dialog */}
            <CampaignContactStatusDialog
                open={statusDialogOpen}
                onClose={() => {
                    setStatusDialogOpen(false);
                    setSelectedContact(null);
                }}
                contact={selectedContact}
                currentStatus={selectedContact?.status}
                onUpdateStatus={handleStatusUpdate}
                campaignId={campaignId}
            />

            {/* Import Dialog */}
            <Dialog
                open={openImportDialog}
                onClose={() => setOpenImportDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Import Contacts</DialogTitle>
                <DialogContent>
                    <ContactImport
                        campaignId={campaignId}
                        onImportComplete={handleImportComplete}
                    />
                </DialogContent>
            </Dialog>
            <WebContactImporter
                open={showWebImporter}
                onClose={() => setShowWebImporter(false)}
                campaignId={campaignId}
                onImportComplete={handleImportComplete}
            />            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CampaignContacts;